import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Card, Typography, Button, Chip, Alert, Input, Tooltip } from "@material-tailwind/react";
import ReactSelect from 'react-select';
import { 
    FaEye, FaTrash, FaCheck, FaClock, FaMobile, FaFilter, FaSyncAlt, 
    FaChevronLeft, FaChevronRight, FaSearch, FaCopy, FaExclamationTriangle, 
    FaRedo, FaList, FaTh, FaSortAmountDown, FaSortAmountUp, FaTimes, FaEllipsisV
} from 'react-icons/fa';

const Messages = ({ rootUrl, token }) => {
    // Core state
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination state
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        pageSize: 25
    });
    
    // Filter state - separate draft filters from applied filters
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        type: '',
        sender: '',
        startDate: '',
        endDate: '',
        processedStatus: ''
    });
    const [appliedFilters, setAppliedFilters] = useState({
        search: '',
        status: '',
        type: '',
        sender: '',
        startDate: '',
        endDate: '',
        processedStatus: ''
    });
    
    // UI state
    const [showFilters, setShowFilters] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedMessages, setSelectedMessages] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [sortBy, setSortBy] = useState('timestamp');
    const [sortOrder, setSortOrder] = useState('desc');
    
    // Action states
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
    const [requeueLoading, setRequeueLoading] = useState(new Set());
    const [copiedMessageId, setCopiedMessageId] = useState(null);
    const [openMenu, setOpenMenu] = useState(null);
    
    // Filter options
    const [messageTypes, setMessageTypes] = useState(['TRANSACTION', 'BALANCE', 'TRANSFER', 'DEPOSIT', 'WITHDRAWAL']);
    const [messageSenders, setMessageSenders] = useState(['MPESA', 'BANK', 'SYSTEM', 'MANUAL']);
    const [, setUnprocessedCount] = useState(0);

    useEffect(() => {
        if (!openMenu) return undefined;

        const closeMenu = () => setOpenMenu(null);

        const handlePointerDown = (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;
            if (target.closest('[data-message-actions-menu="true"]')) return;
            if (target.closest('[data-message-actions-trigger="true"]')) return;
            closeMenu();
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') closeMenu();
        };

        window.addEventListener('scroll', closeMenu, true);
        window.addEventListener('resize', closeMenu);
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown, { passive: true });
        document.addEventListener('keydown', handleEscape);

        return () => {
            window.removeEventListener('scroll', closeMenu, true);
            window.removeEventListener('resize', closeMenu);
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [openMenu]);

    const openActionsMenu = (message, triggerEl) => {
        const rect = triggerEl?.getBoundingClientRect?.();
        if (!rect) {
            setOpenMenu({ messageId: message.id, top: 0, left: 0, openUp: false });
            return;
        }

        const menuWidth = 176;
        const viewportPadding = 12;
        const spaceAbove = rect.top;
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceAbove > 220 && spaceAbove > spaceBelow;
        const left = Math.min(
            window.innerWidth - menuWidth - viewportPadding,
            Math.max(viewportPadding, rect.right - menuWidth)
        );

        setOpenMenu({
            messageId: message.id,
            top: openUp ? rect.top - 8 : rect.bottom + 8,
            left,
            openUp,
        });
    };

    const closeActionsMenu = () => setOpenMenu(null);

    // Fetch messages with proper pagination
    const fetchMessages = useCallback(async (page = 1, newFilters = null, showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            setError(null);

            const filtersToUse = newFilters || appliedFilters;
            const params = new URLSearchParams({
                page: page.toString(),
                limit: pagination.pageSize.toString(),
                sortBy: sortBy,
                sortOrder: sortOrder,
                ...filtersToUse
            });

            const response = await fetch(`${rootUrl}/v2/messages?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            
            if (data.success) {
                setMessages(data.messages || []);
                
                // Update pagination state
                setPagination(prev => ({
                    ...prev,
                    currentPage: data.pagination?.currentPage || page,
                    // Only update totalPages if we have pagination data, otherwise preserve current value
                    totalPages: data.pagination?.totalPages || prev.totalPages,
                    totalRecords: data.pagination?.totalRecords || prev.totalRecords
                }));

                // Update stats
                if (data.stats?.unprocessedCount !== undefined) {
                    setUnprocessedCount(data.stats.unprocessedCount);
                }

                // Update filter options
                if (data.meta) {
                    const apiTypes = data.meta.types || [];
                    const apiSenders = data.meta.senders || [];
                    setMessageTypes(prev => [...new Set([...prev, ...apiTypes])]);
                    setMessageSenders(prev => [...new Set([...prev, ...apiSenders])]);
                } else {
                    const extractedTypes = [...new Set(data.messages.map(msg => msg.type).filter(Boolean))];
                    const extractedSenders = [...new Set(data.messages.map(msg => msg.sender_name || msg.sender).filter(Boolean))];
                    setMessageTypes(prev => [...new Set([...prev, ...extractedTypes])]);
                    setMessageSenders(prev => [...new Set([...prev, ...extractedSenders])]);
                }
            } else {
                throw new Error(data.error || data.message || 'Failed to fetch messages');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [rootUrl, token, pagination.pageSize, appliedFilters, sortBy, sortOrder]);

    // Message actions
    const deleteMessage = async (messageId) => {
        if (!window.confirm('Are you sure you want to delete this message?')) return;

        try {
            const response = await fetch(`${rootUrl}/v2/messages/${messageId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                if (response.status === 400 && errorData.error === "Cannot delete message") {
                    const friendlyMessage = `Cannot delete this message because it is referenced by transactions (${errorData.trans_msg_ref || 'reference ID not available'}). Referenced messages cannot be deleted to maintain data integrity.`;
                    throw new Error(friendlyMessage);
                }
                throw new Error(errorData.error || errorData.message || 'Failed to delete message');
            }

            setMessages(prev => prev.filter(msg => msg.id !== messageId));
            if (selectedMessage && selectedMessage.id === messageId) {
                setShowModal(false);
                setSelectedMessage(null);
            }
            fetchMessages(pagination.currentPage, null, false);
        } catch (err) {
            setError(err.message);
        }
    };

    const markAsUsed = async (messageId, usageData = {}) => {
        try {
            const response = await fetch(`${rootUrl}/v2/messages/${messageId}/mark-used`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(usageData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.message || 'Failed to mark message as used');
            }

            fetchMessages(pagination.currentPage, null, false);
            if (selectedMessage && selectedMessage.id === messageId) {
                setShowModal(false);
                setSelectedMessage(null);
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const requeueMessage = async (messageId) => {
        if (!window.confirm('Are you sure you want to requeue this message for processing?')) return;

        try {
            setRequeueLoading(prev => new Set([...prev, messageId]));
            setError(null);

            const response = await fetch(`${rootUrl}/v2/messages/${messageId}/requeue`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.message || 'Failed to requeue message');
            }

            fetchMessages(pagination.currentPage, null, false);
            if (selectedMessage && selectedMessage.id === messageId) {
                setShowModal(false);
                setSelectedMessage(null);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setRequeueLoading(prev => {
                const newSet = new Set(prev);
                newSet.delete(messageId);
                return newSet;
            });
        }
    };

    const reEvaluateMessage = async (messageId) => {
        if (!window.confirm('Are you sure you want to re-evaluate this processed message? This will reset its evaluation and process it again.')) return;

        try {
            setRequeueLoading(prev => new Set([...prev, messageId]));
            setError(null);

            const response = await fetch(`${rootUrl}/v2/messages/${messageId}/re-evaluate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.message || 'Failed to re-evaluate message');
            }

            fetchMessages(pagination.currentPage, null, false);
            if (selectedMessage && selectedMessage.id === messageId) {
                setShowModal(false);
                setSelectedMessage(null);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setRequeueLoading(prev => {
                const newSet = new Set(prev);
                newSet.delete(messageId);
                return newSet;
            });
        }
    };

    // Bulk operations
    const bulkDeleteMessages = async () => {
        if (selectedMessages.size === 0) {
            setError('No messages selected for deletion');
            return;
        }

        if (!window.confirm(`Are you sure you want to delete ${selectedMessages.size} selected messages? This action cannot be undone.`)) return;

        try {
            setBulkDeleteLoading(true);
            setError(null);

            const response = await fetch(`${rootUrl}/v2/messages/bulk-delete`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    messageIds: Array.from(selectedMessages),
                    confirm: true
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                if (response.status === 400 && errorData.error === "Cannot delete messages") {
                    const referencedCount = errorData.referencedCount || errorData.referencedMessages?.length || 0;
                    const referencedIds = errorData.referencedMessages || [];
                    let friendlyMessage = `Cannot delete ${referencedCount} of the selected messages because they are referenced by transactions.`;
                    if (referencedIds.length > 0) {
                        friendlyMessage += ` Referenced message IDs: ${referencedIds.join(', ')}.`;
                    }
                    friendlyMessage += ` Referenced messages cannot be deleted to maintain data integrity. Please unselect these messages and try again.`;
                    throw new Error(friendlyMessage);
                }
                throw new Error(errorData.error || errorData.message || 'Failed to delete messages');
            }

            const result = await response.json();
            setSelectedMessages(new Set());
            setSelectAll(false);
            fetchMessages(pagination.currentPage, null, false);
            alert(`Successfully deleted ${result.deleted} messages`);
        } catch (err) {
            setError(err.message);
        } finally {
            setBulkDeleteLoading(false);
        }
    };

    // Filter and pagination handlers
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const applyFilters = () => {
        setAppliedFilters(filters);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        fetchMessages(1, filters);
        setShowFilters(false);
    };

    const clearFilters = () => {
        const clearedFilters = {
            search: '',
            status: '',
            type: '',
            sender: '',
            startDate: '',
            endDate: '',
            processedStatus: ''
        };
        setFilters(clearedFilters);
        setAppliedFilters(clearedFilters);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        fetchMessages(1, clearedFilters);
        setShowFilters(false);
    };

    const goToPage = (page) => {
        if (page >= 1 && page <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: page }));
            fetchMessages(page);
        }
    };

    const handlePageSizeChange = (newPageSize) => {
        setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
        fetchMessages(1);
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
        fetchMessages(1);
    };

    // Selection handlers
    const handleMessageSelect = (messageId) => {
        const newSelected = new Set(selectedMessages);
        if (newSelected.has(messageId)) {
            newSelected.delete(messageId);
        } else {
            newSelected.add(messageId);
        }
        setSelectedMessages(newSelected);
        setSelectAll(newSelected.size === messages.length && messages.length > 0);
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedMessages(new Set());
            setSelectAll(false);
        } else {
            setSelectedMessages(new Set(messages.map(msg => msg.id)));
            setSelectAll(true);
        }
    };

    // Utility functions
    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    const formatCompactTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        if (Number.isNaN(date.getTime())) return formatTimestamp(timestamp);
        const pad = (num) => String(num).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    const getStatusColor = (used) => {
        return used ? 'green' : 'blue';
    };

    const getProcessingStatus = (message) => {
        return message.trans_msg_ref ? 'processed' : 'unprocessed';
    };

    const getProcessingStatusColor = (message) => {
        return message.trans_msg_ref ? 'green' : 'red';
    };

    const getTypeIconMeta = (type) => {
        const normalized = (type || '').toUpperCase();
        if (normalized === 'TRANSACTION' || normalized === 'TRANSFER') {
            return { icon: <FaSyncAlt className="h-3 w-3" />, color: 'text-blue-600 bg-blue-50 border-blue-200', label: normalized || 'Unknown type' };
        }
        if (normalized === 'DEPOSIT') {
            return { icon: <FaSortAmountUp className="h-3 w-3" />, color: 'text-green-600 bg-green-50 border-green-200', label: normalized };
        }
        if (normalized === 'WITHDRAWAL') {
            return { icon: <FaSortAmountDown className="h-3 w-3" />, color: 'text-red-600 bg-red-50 border-red-200', label: normalized };
        }
        if (normalized === 'BALANCE') {
            return { icon: <FaMobile className="h-3 w-3" />, color: 'text-purple-600 bg-purple-50 border-purple-200', label: normalized };
        }
        return { icon: <FaClock className="h-3 w-3" />, color: 'text-gray-600 bg-gray-50 border-gray-200', label: normalized || 'Unknown type' };
    };

    const isUnprocessed = (message) => {
        return !message.trans_msg_ref;
    };

    const canReEvaluate = (message) => {
        return message.trans_msg_ref && !message.used;
    };

    const copyToClipboard = async (text, messageId) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedMessageId(messageId);
            setTimeout(() => setCopiedMessageId(null), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopiedMessageId(messageId);
            setTimeout(() => setCopiedMessageId(null), 2000);
        }
    };

    // Helper to check if any filters are applied
    const hasActiveFilters = () => {
        return Object.values(appliedFilters).some(value => value !== '');
    };

    // React-select custom styles
    const selectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '40px',
            borderColor: state.isFocused ? '#FF5733' : '#d1d5db',
            boxShadow: state.isFocused ? '0 0 0 1px #FF5733' : 'none',
            '&:hover': {
                borderColor: '#FF5733'
            }
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#fff8f5' : 'white',
            color: state.isSelected ? 'white' : '#2a2e30',
            '&:active': {
                backgroundColor: '#e04a2b'
            }
        })
    };

    // Effects
    useEffect(() => {
        fetchMessages(1);
    }, [fetchMessages]);

    // Set default view mode based on screen size on initial load and enforce grid on mobile
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            // Force grid view on mobile screens
            if (mobile) {
                setViewMode('grid');
            }
        };
        
        // Set initial view mode based on screen size (only on mount)
        const mobile = window.innerWidth < 768;
        setIsMobile(mobile);
        if (mobile) {
            setViewMode('grid');
        }
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#FF5733]"></div>
            </div>
        );
    }

    return (
        <div className="p-3 sm:p-6 bg-[#fff8f5] min-h-screen">
            {/* Page Title */}
            <div className="text-center mb-6">
                <Typography variant="h3" className="text-[#2a2e30] font-bold text-3xl sm:text-4xl mb-2">
                    All Message Inbox
                </Typography>
                <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full"></div>
            </div>

            {/* Header */}
            <div className="mb-4 sm:mb-8">
              
                {/* Controls Bar */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 sm:gap-4 mb-4 sm:mb-6 bg-white border border-gray-200 rounded-sm p-2 sm:p-3 shadow-none">
                    <div className="flex flex-wrap gap-2 sm:justify-start justify-end w-full sm:w-auto">
                        {selectedMessages.size > 0 && (
                            <Button
                                onClick={bulkDeleteMessages}
                                disabled={bulkDeleteLoading}
                                color="red"
                                size="sm"
                                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-sm px-3 py-2 shadow-none normal-case"
                            >
                                {bulkDeleteLoading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                ) : (
                                    <FaTrash />
                                )}
                                Delete Selected ({selectedMessages.size})
                            </Button>
                        )}
                        <Button
                            onClick={() => setShowFilters(!showFilters)}
                            variant="outlined"
                            size="sm"
                            className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none border border-slate-200 normal-case"
                        >
                            <FaFilter /> Filters
                        </Button>
                        <Button
                            onClick={() => fetchMessages(pagination.currentPage)}
                            className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-3 py-2 shadow-none flex items-center gap-2 normal-case"
                            size="sm"
                        >
                            <FaSyncAlt />
                            <span>Refresh</span>
                        </Button>
                    </div>

                    <div className="flex items-center gap-3 sm:justify-end justify-end w-full sm:w-auto">
                        <div className="hidden sm:flex items-center gap-2">
                            <Button
                                variant={viewMode === 'table' ? 'filled' : 'outlined'}
                                size="sm"
                                onClick={() => setViewMode('table')}
                                className={`p-2 rounded-sm shadow-none flex items-center justify-center min-w-[36px] h-9 border normal-case ${
                                    viewMode === 'table'
                                        ? 'bg-[#FF5733] hover:bg-[#e64520] text-white border-[#FF5733]'
                                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'
                                }`}
                            >
                                <FaList />
                            </Button>
                            <Button
                                variant={viewMode === 'grid' ? 'filled' : 'outlined'}
                                size="sm"
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded-sm shadow-none flex items-center justify-center min-w-[36px] h-9 border normal-case ${
                                    viewMode === 'grid'
                                        ? 'bg-[#FF5733] hover:bg-[#e64520] text-white border-[#FF5733]'
                                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'
                                }`}
                            >
                                <FaTh />
                            </Button>
                        </div>
                        <div className="flex items-center gap-2">
                            <Typography variant="small" color="blue-gray" className="hidden sm:inline text-xs font-semibold uppercase tracking-wide">Per page</Typography>
                            <select
                                value={pagination.pageSize}
                                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                                className="w-16 h-9 px-2 text-sm border border-slate-200 rounded-sm bg-white text-slate-700 focus:outline-none focus:border-[#FF5733] focus:ring-1 focus:ring-[#FF5733]"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Applied Filters Display */}
            {hasActiveFilters() && (
                <Card className="p-3 sm:p-4 mb-4 sm:mb-6 bg-white shadow-md rounded-xl border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                        <Typography variant="small" className="text-[#2a2e30] font-semibold">
                            Active Filters:
                        </Typography>
                        <Button
                            onClick={clearFilters}
                            variant="text"
                            size="sm"
                            className="text-red-600 hover:text-red-700 p-1"
                        >
                            <FaTimes className="w-3 h-3" />
                        </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {appliedFilters.search && (
                            <Chip
                                value={`Search: ${appliedFilters.search}`}
                                onClose={() => {
                                    const newFilters = { ...appliedFilters, search: '' };
                                    setAppliedFilters(newFilters);
                                    setFilters({ ...filters, search: '' });
                                    fetchMessages(1, newFilters);
                                }}
                                className="bg-[#FF5733] text-white"
                            />
                        )}
                        {appliedFilters.status && (
                            <Chip
                                value={`Status: ${appliedFilters.status}`}
                                onClose={() => {
                                    const newFilters = { ...appliedFilters, status: '' };
                                    setAppliedFilters(newFilters);
                                    setFilters({ ...filters, status: '' });
                                    fetchMessages(1, newFilters);
                                }}
                                className="bg-[#FF5733] text-white"
                            />
                        )}
                        {appliedFilters.type && (
                            <Chip
                                value={`Type: ${appliedFilters.type}`}
                                onClose={() => {
                                    const newFilters = { ...appliedFilters, type: '' };
                                    setAppliedFilters(newFilters);
                                    setFilters({ ...filters, type: '' });
                                    fetchMessages(1, newFilters);
                                }}
                                className="bg-[#FF5733] text-white"
                            />
                        )}
                        {appliedFilters.processedStatus && (
                            <Chip
                                value={`Processing: ${appliedFilters.processedStatus}`}
                                onClose={() => {
                                    const newFilters = { ...appliedFilters, processedStatus: '' };
                                    setAppliedFilters(newFilters);
                                    setFilters({ ...filters, processedStatus: '' });
                                    fetchMessages(1, newFilters);
                                }}
                                className="bg-[#FF5733] text-white"
                            />
                        )}
                        {appliedFilters.sender && (
                            <Chip
                                value={`Sender: ${appliedFilters.sender}`}
                                onClose={() => {
                                    const newFilters = { ...appliedFilters, sender: '' };
                                    setAppliedFilters(newFilters);
                                    setFilters({ ...filters, sender: '' });
                                    fetchMessages(1, newFilters);
                                }}
                                className="bg-[#FF5733] text-white"
                            />
                        )}
                        {appliedFilters.startDate && (
                            <Chip
                                value={`From: ${new Date(appliedFilters.startDate).toLocaleDateString()}`}
                                onClose={() => {
                                    const newFilters = { ...appliedFilters, startDate: '' };
                                    setAppliedFilters(newFilters);
                                    setFilters({ ...filters, startDate: '' });
                                    fetchMessages(1, newFilters);
                                }}
                                className="bg-[#FF5733] text-white"
                            />
                        )}
                        {appliedFilters.endDate && (
                            <Chip
                                value={`To: ${new Date(appliedFilters.endDate).toLocaleDateString()}`}
                                onClose={() => {
                                    const newFilters = { ...appliedFilters, endDate: '' };
                                    setAppliedFilters(newFilters);
                                    setFilters({ ...filters, endDate: '' });
                                    fetchMessages(1, newFilters);
                                }}
                                className="bg-[#FF5733] text-white"
                            />
                        )}
                    </div>
                </Card>
            )}

            {/* Filters Panel */}
            {showFilters && (
                <Card className="p-4 sm:p-6 mb-4 sm:mb-6 bg-white shadow-lg rounded-xl border border-gray-100">
                    <Typography variant="h6" className="mb-4 text-[#2a2e30] font-semibold">Filters</Typography>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
                        <div>
                            <Input
                                label="Search messages"
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                icon={<FaSearch />}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <ReactSelect
                                value={filters.status ? { value: filters.status, label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1) } : null}
                                onChange={(option) => handleFilterChange('status', option ? option.value : '')}
                                options={[
                                    { value: 'pending', label: 'Pending' },
                                    { value: 'used', label: 'Used' }
                                ]}
                                isClearable
                                placeholder="All Status"
                                styles={selectStyles}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                            <ReactSelect
                                value={filters.type ? { value: filters.type, label: filters.type } : null}
                                onChange={(option) => handleFilterChange('type', option ? option.value : '')}
                                options={messageTypes.map(type => ({ value: type, label: type }))}
                                isClearable
                                placeholder="All Types"
                                styles={selectStyles}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Processing Status</label>
                            <ReactSelect
                                value={filters.processedStatus ? { value: filters.processedStatus, label: filters.processedStatus.charAt(0).toUpperCase() + filters.processedStatus.slice(1) } : null}
                                onChange={(option) => handleFilterChange('processedStatus', option ? option.value : '')}
                                options={[
                                    { value: 'processed', label: 'Processed' },
                                    { value: 'unprocessed', label: 'Unprocessed' }
                                ]}
                                isClearable
                                placeholder="All Processing Status"
                                styles={selectStyles}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Sender</label>
                            <ReactSelect
                                value={filters.sender ? { value: filters.sender, label: filters.sender } : null}
                                onChange={(option) => handleFilterChange('sender', option ? option.value : '')}
                                options={messageSenders.map(sender => ({ value: sender, label: sender }))}
                                isClearable
                                placeholder="All Senders"
                                styles={selectStyles}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>
                        <div>
                            <Input
                                type="datetime-local"
                                label="Start Date"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            />
                        </div>
                        <div>
                            <Input
                                type="datetime-local"
                                label="End Date"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                        <Button onClick={applyFilters} size="sm" className="bg-[#FF5733] hover:bg-[#e04a2b] w-full sm:w-auto">
                            Apply Filters
                        </Button>
                        <Button onClick={clearFilters} variant="outlined" size="sm" className="w-full sm:w-auto">
                            Clear All
                        </Button>
                    </div>
                </Card>
            )}

            {/* Error Alert */}
            {error && (
                <Alert color="red" className="mb-6">
                    {error}
                </Alert>
            )}

            {/* Stats */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                <Typography variant="small" color="blue-gray" className="font-medium">
                    Showing {messages.length} of {pagination.totalRecords} messages
                </Typography>
                <div className="flex flex-wrap items-center gap-2">
                    <Typography variant="small" color="blue-gray" className="font-medium">Sort by:</Typography>
                    <Button
                        variant="text"
                        size="sm"
                        onClick={() => handleSort('timestamp')}
                        className={`flex items-center gap-1 ${sortBy === 'timestamp' ? 'text-[#FF5733] font-semibold' : ''}`}
                    >
                        Date {sortBy === 'timestamp' && (sortOrder === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />)}
                    </Button>
                    <Button
                        variant="text"
                        size="sm"
                        onClick={() => handleSort('type')}
                        className={`flex items-center gap-1 ${sortBy === 'type' ? 'text-[#FF5733] font-semibold' : ''}`}
                    >
                        Type {sortBy === 'type' && (sortOrder === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />)}
                    </Button>
                </div>
            </div>

            {/* Messages Display */}
            {(viewMode === 'table' && !isMobile) ? (
                <Card className="w-full overflow-hidden bg-white shadow-lg rounded-xl border border-gray-100">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[960px] table-auto text-left">
                            <thead>
                                <tr className="border-b border-blue-gray-100 bg-[#EDE4DA]">
                                    <th className="p-2 w-[40px] text-center">
                                        <input
                                            type="checkbox"
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                            className="w-4 h-4 text-[#FF5733] bg-gray-100 border-gray-300 rounded focus:ring-[#FF5733] cursor-pointer"
                                        />
                                    </th>
                                    <th className="p-2 w-[108px]">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none">
                                            Sender
                                        </Typography>
                                    </th>
                                    <th className="p-2 hidden sm:table-cell w-[180px]">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none">
                                            Message
                                        </Typography>
                                    </th>
                                    <th className="p-2 w-[52px] text-center">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                                            Type
                                        </Typography>
                                    </th>
                                    <th className="p-2 w-[52px] text-center">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                                            Status
                                        </Typography>
                                    </th>
                                    <th className="p-2 w-[52px] text-center">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                                            Processing
                                        </Typography>
                                    </th>
                                    <th className="p-2 hidden md:table-cell w-[116px]">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none">
                                            Timestamp
                                        </Typography>
                                    </th>
                                    <th className="p-2 w-[60px] text-center">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none">
                                            Actions
                                        </Typography>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {messages.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="p-8 text-center">
                                            <Typography color="blue-gray" className="font-normal">
                                                No messages found
                                            </Typography>
                                        </td>
                                    </tr>
                                ) : (
                                    messages.map((message) => (
                                        <tr key={message.id} className="border-b border-blue-gray-50 hover:bg-blue-gray-50/50 transition-colors">
                                            <td className="p-2 w-[40px] text-center align-middle">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedMessages.has(message.id)}
                                                    onChange={() => handleMessageSelect(message.id)}
                                                    className="w-4 h-4 text-[#FF5733] bg-gray-100 border-gray-300 rounded focus:ring-[#FF5733] cursor-pointer"
                                                />
                                            </td>
                                            <td className="p-2 align-middle w-[108px] max-w-[108px]">
                                                <Tooltip content={`ID: ${message.id}`} placement="top">
                                                    <div className="flex items-center gap-2 cursor-help">
                                                        <FaMobile className="text-[#FF5733] flex-shrink-0" />
                                                        <Typography variant="small" color="blue-gray" className="font-medium truncate text-xs">
                                                            {message.sender || 'Unknown'}
                                                        </Typography>
                                                    </div>
                                                </Tooltip>
                                            </td>
                                            <td className="p-2 hidden sm:table-cell align-middle w-[180px] max-w-[180px]">
                                                <Tooltip content={message.text || 'No text content'} placement="top">
                                                    <Typography variant="small" color="blue-gray" className="font-normal truncate cursor-help text-xs">
                                                        {message.text || 'No text content'}
                                                    </Typography>
                                                </Tooltip>
                                            </td>
                                            <td className="p-2 w-[52px] text-center align-middle">
                                                {(() => {
                                                    const typeMeta = getTypeIconMeta(message.type);
                                                    return (
                                                        <Tooltip content={`Type: ${typeMeta.label}`} placement="top">
                                                            <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full border ${typeMeta.color}`}>
                                                                {typeMeta.icon}
                                                            </span>
                                                        </Tooltip>
                                                    );
                                                })()}
                                            </td>
                                            <td className="p-2 w-[52px] text-center align-middle">
                                                <Tooltip content={`Status: ${message.used ? 'Used' : 'Pending'}`} placement="top">
                                                    <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full border ${message.used ? 'text-green-600 bg-green-50 border-green-200' : 'text-blue-600 bg-blue-50 border-blue-200'}`}>
                                                        {message.used ? <FaCheck className="h-3 w-3" /> : <FaClock className="h-3 w-3" />}
                                                    </span>
                                                </Tooltip>
                                            </td>
                                            <td className="p-2 w-[52px] text-center align-middle">
                                                <Tooltip content={`Processing: ${getProcessingStatus(message)}`} placement="top">
                                                    <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full border ${isUnprocessed(message) ? 'text-red-600 bg-red-50 border-red-200' : 'text-green-600 bg-green-50 border-green-200'}`}>
                                                        {isUnprocessed(message) ? <FaExclamationTriangle className="h-3 w-3" /> : <FaCheck className="h-3 w-3" />}
                                                    </span>
                                                </Tooltip>
                                            </td>
                                            <td className="p-2 hidden md:table-cell align-middle w-[116px]">
                                                <Tooltip content={formatTimestamp(message.timestamp)} placement="top">
                                                    <Typography variant="small" color="blue-gray" className="font-normal text-xs truncate cursor-help max-w-[116px]">
                                                        {formatCompactTimestamp(message.timestamp)}
                                                    </Typography>
                                                </Tooltip>
                                            </td>
                                            <td className="relative p-2 w-[60px] text-center align-middle">
                                                <div className="flex items-center justify-center">
                                                    <Tooltip content="Actions" placement="top">
                                                        <Button
                                                            size="sm"
                                                            variant="text"
                                                            className="w-8 h-8 p-2 rounded-full border border-slate-400 bg-white text-slate-700 hover:bg-slate-100 hover:border-slate-500"
                                                            data-message-actions-trigger="true"
                                                            onClick={(e) => openActionsMenu(message, e.currentTarget)}
                                                            aria-label={`Actions for message ${message.id}`}
                                                        >
                                                            <FaEllipsisV className="h-3 w-3" />
                                                        </Button>
                                                    </Tooltip>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {messages.map((message) => (
                        <Card key={message.id} className="p-4 sm:p-5 bg-white shadow-lg hover:shadow-xl transition-all duration-200 rounded-xl border border-gray-100">
                            <div className="flex items-start justify-between mb-3">
                                <Tooltip content={`ID: ${message.id}`} placement="top">
                                    <div className="flex items-center gap-2 cursor-help flex-1 min-w-0">
                                        <input
                                            type="checkbox"
                                            checked={selectedMessages.has(message.id)}
                                            onChange={() => handleMessageSelect(message.id)}
                                            className="w-4 h-4 text-[#FF5733] bg-gray-100 border-gray-300 rounded focus:ring-[#FF5733] cursor-pointer flex-shrink-0"
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <FaMobile className="text-[#FF5733] flex-shrink-0" />
                                        <Typography variant="small" className="font-semibold truncate">
                                            {message.sender || 'Unknown'}
                                        </Typography>
                                    </div>
                                </Tooltip>
                                <div className="flex gap-1 flex-shrink-0 ml-2">
                                    <Tooltip content="View" placement="top">
                                        <Button
                                            size="sm"
                                            variant="text"
                                            className="w-8 h-8 p-2 rounded-full border border-blue-100 text-blue-600 hover:bg-blue-50 transition-colors"
                                            onClick={() => {
                                                setSelectedMessage(message);
                                                setShowModal(true);
                                            }}
                                            aria-label={`View message ${message.id}`}
                                        >
                                            <FaEye className="h-3 w-3" />
                                        </Button>
                                    </Tooltip>

                                    <Tooltip content="Delete" placement="top">
                                        <Button
                                            size="sm"
                                            variant="text"
                                            className="w-8 h-8 p-2 rounded-full border border-red-100 text-red-600 hover:bg-red-50 transition-colors"
                                            onClick={() => deleteMessage(message.id)}
                                            aria-label={`Delete message ${message.id}`}
                                        >
                                            <FaTrash className="h-3 w-3" />
                                        </Button>
                                    </Tooltip>
                                </div>
                            </div>
                            
                            <div className="mb-3">
                                <Typography variant="small" color="blue-gray" className="font-normal line-clamp-3">
                                    {message.text || 'No text content'}
                                </Typography>
                            </div>
                            
                            <div className="flex flex-wrap gap-2 mb-3">
                                {(() => {
                                    const typeMeta = getTypeIconMeta(message.type);
                                    return (
                                        <Tooltip content={`Type: ${typeMeta.label}`} placement="top">
                                            <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full border ${typeMeta.color}`}>
                                                {typeMeta.icon}
                                            </span>
                                        </Tooltip>
                                    );
                                })()}
                                <Tooltip content={`Status: ${message.used ? 'Used' : 'Pending'}`} placement="top">
                                    <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full border ${message.used ? 'text-green-600 bg-green-50 border-green-200' : 'text-blue-600 bg-blue-50 border-blue-200'}`}>
                                        {message.used ? <FaCheck className="h-3 w-3" /> : <FaClock className="h-3 w-3" />}
                                    </span>
                                </Tooltip>
                                <Tooltip content={`Processing: ${getProcessingStatus(message)}`} placement="top">
                                    <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full border ${isUnprocessed(message) ? 'text-red-600 bg-red-50 border-red-200' : 'text-green-600 bg-green-50 border-green-200'}`}>
                                        {isUnprocessed(message) ? <FaExclamationTriangle className="h-3 w-3" /> : <FaCheck className="h-3 w-3" />}
                                    </span>
                                </Tooltip>
                            </div>
                            
                            <Typography variant="small" color="blue-gray" className="font-normal mb-3">
                                {formatTimestamp(message.timestamp)}
                            </Typography>
                            
                            <div className="flex flex-col sm:flex-row gap-2 mt-3">
                                {!message.used && (
                                    <Button
                                        size="sm"
                                        variant="outlined"
                                        color="green"
                                        onClick={() => markAsUsed(message.id)}
                                        className="flex-1 flex items-center justify-center gap-1"
                                    >
                                        <FaCheck className="h-3 w-3" />
                                        <span className="hidden sm:inline">Mark Used</span>
                                        <span className="sm:hidden">Used</span>
                                    </Button>
                                )}
                                {isUnprocessed(message) && (
                                    <Button
                                        size="sm"
                                        variant="outlined"
                                        color="red"
                                        onClick={() => requeueMessage(message.id)}
                                        disabled={requeueLoading.has(message.id)}
                                        className="flex-1 flex items-center justify-center gap-1"
                                    >
                                        {requeueLoading.has(message.id) ? (
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-500"></div>
                                        ) : (
                                            <>
                                                <FaRedo className="h-3 w-3" />
                                                <span className="hidden sm:inline">Requeue</span>
                                                <span className="sm:hidden">Queue</span>
                                            </>
                                        )}
                                    </Button>
                                )}
                                {canReEvaluate(message) && (
                                    <Button
                                        size="sm"
                                        variant="outlined"
                                        color="purple"
                                        onClick={() => reEvaluateMessage(message.id)}
                                        disabled={requeueLoading.has(message.id)}
                                        className="flex-1 flex items-center justify-center gap-1"
                                    >
                                        {requeueLoading.has(message.id) ? (
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-500"></div>
                                        ) : (
                                            <>
                                                <FaRedo className="h-3 w-3" />
                                                <span className="hidden sm:inline">Re-evaluate</span>
                                                <span className="sm:hidden">Eval</span>
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
                <div className="flex flex-col sm:flex-row justify-between items-center mt-6 sm:mt-8 gap-3 sm:gap-4">
                    <Typography variant="small" color="blue-gray" className="font-medium text-center sm:text-left">
                        Page {pagination.currentPage} of {pagination.totalPages}
                    </Typography>
                    <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                        <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-start">
                            <Button
                                variant="outlined"
                                size="sm"
                                onClick={() => goToPage(pagination.currentPage - 1)}
                                disabled={pagination.currentPage === 1}
                                className="flex items-center gap-1 flex-1 sm:flex-none min-w-[100px] sm:min-w-0"
                            >
                                <FaChevronLeft className="w-3 h-3" />
                                <span className="hidden sm:inline">Previous</span>
                                <span className="sm:hidden">Prev</span>
                            </Button>

                            {/* Page numbers - hide on very small screens, show dots on mobile */}
                            <div className="hidden xs:flex gap-1">
                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                    let pageNumber;
                                    if (pagination.totalPages <= 5) {
                                        pageNumber = i + 1;
                                    } else if (pagination.currentPage <= 3) {
                                        pageNumber = i + 1;
                                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                        pageNumber = pagination.totalPages - 4 + i;
                                    } else {
                                        pageNumber = pagination.currentPage - 2 + i;
                                    }

                                    return (
                                        <Button
                                            key={pageNumber}
                                            variant={pagination.currentPage === pageNumber ? "filled" : "outlined"}
                                            size="sm"
                                            onClick={() => goToPage(pageNumber)}
                                            className={`min-w-[2rem] px-2 sm:px-3 ${
                                                pagination.currentPage === pageNumber 
                                                    ? 'bg-[#FF5733] hover:bg-[#e04a2b]' 
                                                    : ''
                                            }`}
                                        >
                                            {pageNumber}
                                        </Button>
                                    );
                                })}
                            </div>
                            
                            {/* Mobile page indicator */}
                            <div className="xs:hidden px-2">
                                <Typography variant="small" color="blue-gray" className="font-medium">
                                    {pagination.currentPage}/{pagination.totalPages}
                                </Typography>
                            </div>

                            <Button
                                variant="outlined"
                                size="sm"
                                onClick={() => goToPage(pagination.currentPage + 1)}
                                disabled={pagination.currentPage === pagination.totalPages}
                                className="flex items-center gap-1 flex-1 sm:flex-none min-w-[100px] sm:min-w-0"
                            >
                                <span className="hidden sm:inline">Next</span>
                                <span className="sm:hidden">Next</span>
                                <FaChevronRight className="w-3 h-3" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Message Details Modal */}
            {showModal && selectedMessage && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100">
                        <div className="flex justify-between items-center mb-4">
                            <Typography variant="h5" color="blue-gray">
                                Message Details
                            </Typography>
                            <Button
                                variant="text"
                                onClick={() => setShowModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ×
                            </Button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <Typography variant="small" color="blue-gray" className="font-semibold mb-1">
                                    Message ID:
                                </Typography>
                                <Typography variant="small" color="blue-gray" className="font-mono bg-gray-100 p-2 rounded">
                                    {selectedMessage.id}
                                </Typography>
                            </div>

                            <div>
                                <Typography variant="small" color="blue-gray" className="font-semibold mb-1">
                                    Sender:
                                </Typography>
                                <Typography variant="small" color="blue-gray">
                                    {selectedMessage.sender || 'Unknown'}
                                </Typography>
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <Typography variant="small" color="blue-gray" className="font-semibold">
                                        Message Text:
                                    </Typography>
                                    <Button
                                        variant="text"
                                        size="sm"
                                        onClick={() => copyToClipboard(selectedMessage.text || 'No text content', selectedMessage.id)}
                                        className={`p-1 transition-all duration-200 ${
                                            copiedMessageId === selectedMessage.id
                                                ? 'text-green-500'
                                                : 'text-gray-500 hover:text-blue-500'
                                        }`}
                                        title={copiedMessageId === selectedMessage.id ? 'Copied!' : 'Copy to clipboard'}
                                    >
                                        {copiedMessageId === selectedMessage.id ? (
                                            <FaCheck className="w-4 h-4" />
                                        ) : (
                                            <FaCopy className="w-4 h-4" />
                                        )}
                                    </Button>
                                </div>
                                <Typography variant="small" color="blue-gray" className="bg-gray-100 p-3 rounded">
                                    {selectedMessage.text || 'No text content'}
                                </Typography>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Typography variant="small" color="blue-gray" className="font-semibold mb-1">
                                        Type:
                                    </Typography>
                                    <Typography variant="small" color="blue-gray">
                                        {selectedMessage.type || 'Unknown'}
                                    </Typography>
                                </div>

                                <div>
                                    <Typography variant="small" color="blue-gray" className="font-semibold mb-1">
                                        Status:
                                    </Typography>
                                    <Chip
                                        variant="ghost"
                                        size="sm"
                                        value={selectedMessage.used ? 'Used' : 'Pending'}
                                        color={getStatusColor(selectedMessage.used)}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Typography variant="small" color="blue-gray" className="font-semibold mb-1">
                                        Processing Status:
                                    </Typography>
                                    <Chip
                                        variant="ghost"
                                        size="sm"
                                        value={getProcessingStatus(selectedMessage)}
                                        color={getProcessingStatusColor(selectedMessage)}
                                    />
                                </div>

                                {selectedMessage.last_processed_at && (
                                    <div>
                                        <Typography variant="small" color="blue-gray" className="font-semibold mb-1">
                                            Last Processed:
                                        </Typography>
                                        <Typography variant="small" color="blue-gray">
                                            {formatTimestamp(selectedMessage.last_processed_at)}
                                        </Typography>
                                    </div>
                                )}
                            </div>

                            <div>
                                <Typography variant="small" color="blue-gray" className="font-semibold mb-1">
                                    Received:
                                </Typography>
                                <Typography variant="small" color="blue-gray">
                                    {formatTimestamp(selectedMessage.timestamp)}
                                </Typography>
                            </div>

                            {selectedMessage.used && (
                                <div className="border-t pt-4">
                                    <Typography variant="small" color="blue-gray" className="font-semibold mb-2">
                                        Usage Information:
                                    </Typography>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        {selectedMessage.used_car && (
                                            <div>
                                                <span className="font-medium">Car:</span> {selectedMessage.used_car}
                                            </div>
                                        )}
                                        {selectedMessage.used_driver && (
                                            <div>
                                                <span className="font-medium">Driver:</span> {selectedMessage.used_driver}
                                            </div>
                                        )}
                                        {selectedMessage.used_at && (
                                            <div>
                                                <span className="font-medium">Used At:</span> {formatTimestamp(selectedMessage.used_at)}
                                            </div>
                                        )}
                                        {selectedMessage.used_rule && (
                                            <div className="col-span-2">
                                                <span className="font-medium">Rule:</span> {selectedMessage.used_rule}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col sm:flex-row justify-end gap-2 mt-6">
                            {!selectedMessage.used && (
                                <Button
                                    color="green"
                                    onClick={() => markAsUsed(selectedMessage.id)}
                                    className="w-full sm:w-auto"
                                >
                                    Mark as Used
                                </Button>
                            )}
                            {isUnprocessed(selectedMessage) && (
                                <Button
                                    color="red"
                                    onClick={() => requeueMessage(selectedMessage.id)}
                                    disabled={requeueLoading.has(selectedMessage.id)}
                                    className="w-full sm:w-auto"
                                >
                                    {requeueLoading.has(selectedMessage.id) ? (
                                        <div className="flex items-center gap-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Requeuing...
                                        </div>
                                    ) : (
                                        'Requeue for Processing'
                                    )}
                                </Button>
                            )}
                            {canReEvaluate(selectedMessage) && (
                                <Button
                                    color="purple"
                                    onClick={() => reEvaluateMessage(selectedMessage.id)}
                                    disabled={requeueLoading.has(selectedMessage.id)}
                                    className="w-full sm:w-auto"
                                >
                                    {requeueLoading.has(selectedMessage.id) ? (
                                        <div className="flex items-center gap-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Re-evaluating...
                                        </div>
                                    ) : (
                                        'Re-evaluate Message'
                                    )}
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                onClick={() => setShowModal(false)}
                                className="w-full sm:w-auto"
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {openMenu ? createPortal(
                <div
                    data-message-actions-menu="true"
                    className="fixed w-44 overflow-hidden rounded-md border border-slate-200 bg-white shadow-2xl"
                    style={{
                        top: `${openMenu.top}px`,
                        left: `${openMenu.left}px`,
                        transform: openMenu.openUp ? 'translateY(-100%)' : 'none',
                        zIndex: 99999,
                    }}
                >
                    {(() => {
                        const message = messages.find((row) => row.id === openMenu.messageId);
                        if (!message) return null;

                        return (
                            <>
                                <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => { setSelectedMessage(message); setShowModal(true); closeActionsMenu(); }}>
                                    <FaEye className="h-3 w-3 text-blue-600" />
                                    <span>View message</span>
                                </button>
                                {!message.used && (
                                    <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => { markAsUsed(message.id); closeActionsMenu(); }}>
                                        <FaCheck className="h-3 w-3 text-green-600" />
                                        <span>Mark as used</span>
                                    </button>
                                )}
                                {isUnprocessed(message) && (
                                    <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => { requeueMessage(message.id); closeActionsMenu(); }}>
                                        <FaRedo className="h-3 w-3 text-red-600" />
                                        <span>Requeue</span>
                                    </button>
                                )}
                                {canReEvaluate(message) && (
                                    <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => { reEvaluateMessage(message.id); closeActionsMenu(); }}>
                                        <FaRedo className="h-3 w-3 text-purple-600" />
                                        <span>Re-evaluate</span>
                                    </button>
                                )}
                                <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => { deleteMessage(message.id); closeActionsMenu(); }}>
                                    <FaTrash className="h-3 w-3 text-red-600" />
                                    <span>Delete</span>
                                </button>
                            </>
                        );
                    })()}
                </div>,
                document.body
            ) : null}
        </div>
    );
};

export default Messages;
